import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { formatClubInstant, CLUB_LONG_DATE, CLUB_DATE_TIME } from "../../utils/clubTime";
import { deleteEmailMessages, getMemberMessages, getMessagePreview, sendEmail, sendMemberSms } from "../../api/emailAPI";
import { useHasLayeredPermission } from "../../hooks/useHasPermission";
import { renderTemplateForMember, getQuickReplyTemplates } from "../../api/SettingsAPI";
import { draftMemberEmail, MemberEmailIntent, AIDraftTone } from "../../api/aiAPI";
import axios from "../../api/axiosInstance";
import apiConfig from "../../api/config";
import { getTenantId, getAuthSource } from "../../api/apiHelpers";
import { captureError } from "../../utils/sentry";
import { useSelector } from "react-redux";
import { selectMembershipDetails } from "../../store/slices/membershipSlice";
import { selectUser } from "../../store/slices/authSlice";
import { selectClubName } from "../../store/slices/settingsSlice";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import ConfirmNotesDeleteModal from "../common/ConfirmNotesDeleteModal";
import { useTheme } from "../../context/ThemeContext";
import { getCardBackground, getPrimaryText, getSecondaryText, getLightBorder, getInputStyles } from "../../utils/themeUtils";
import ReactQuill from "../ui/RichTextEditor";
import "react-quill/dist/quill.snow.css";
import MediaLibrary from "../common/MediaLibrary";
import { uploadMediaFile } from "../../api/storageUploadsAPI";
import Tooltip from "../common/Tooltip";
import { statusLabel } from "../../utils/statusLabel";
import { Button } from "../ui";

// Helper function to get status badge styling
const getStatusBadgeClasses = (status: string | null, isDarkMode: boolean) => {
  switch (status) {
    case "delivered":
      return isDarkMode
        ? "bg-green-900 text-green-200"
        : "bg-green-100 text-green-800";
    case "sent":
      return isDarkMode
        ? "bg-blue-900 text-blue-200"
        : "bg-blue-100 text-blue-800";
    case "bounced":
    case "delivery_failed":
      return isDarkMode
        ? "bg-red-900 text-red-200"
        : "bg-red-100 text-red-800";
    case "spam_complaint":
      return isDarkMode
        ? "bg-orange-900 text-orange-200"
        : "bg-orange-100 text-orange-800";
    // A text waiting for the family's morning (quiet hours, PM 9da49135).
    case "held":
      return isDarkMode
        ? "bg-amber-900 text-amber-200"
        : "bg-amber-100 text-amber-800";
    default:
      return isDarkMode
        ? "bg-gray-700 text-gray-200"
        : "bg-gray-100 text-gray-800";
  }
};

// Helper to format status label
const formatStatusLabel = (status: string | null) => {
  if (!status) return "Sent";
  if (status === "held") return "Held until morning";
  return statusLabel(status);
};

// Who a logged message is from. Staff-written messages carry the sender's
// name; the automatic ones (invoices, receipts, reminders) carry an
// email_type instead, and used to read "Sent by Unknown".
const getSenderLabel = (msg: any, t: any) => {
  const meta = msg?.metadata;
  const staffName = `${meta?.first_name || ""} ${meta?.last_name || ""}`.trim();
  if (staffName) return staffName;
  if (meta?.direction === "inbound" && meta?.sender_name) return meta.sender_name;
  if (meta?.email_type) return t('messages:history.automated', 'Automatic email');
  return t('messages:history.unknown');
};

const MessagesHistoryPage = ({ memberId }: any) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation(['messages', 'common']);
  const [messages, setMessages] = useState<any>([]);
  const [visibleCount, setVisibleCount] = useState(1);
  const [loading, setLoading] = useState(false);
  // Email states
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [sending, setSending] = useState(false);
  const [settings, setSettings] = useState<any>(null);
  // HELPER
  const token = localStorage.getItem("authToken");
  const source = localStorage.getItem("authSource");
  const membership: any = useSelector(selectMembershipDetails);
  const userDetails: any = useSelector(selectUser);
  const clubName = useSelector(selectClubName);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deletemessageId, setDeleteMessageId] = useState<any>();
  const [previewMessage, setPreviewMessage] = useState<any>(null);
  const [previewHtml, setPreviewHtml] = useState<string>("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const quillRef = useRef<ReactQuill>(null);
  const [mediaLibraryOpen, setMediaLibraryOpen] = useState(false);
  const [loadingPreset, setLoadingPreset] = useState<string | null>(null);

  // Recipient picker state — staff sending from the member profile can
  // choose exactly who receives this message. Alternates default ON
  // (matches existing CC behavior); additional contacts default OFF
  // because adding extra people to a single message is opt-in.
  const [includeAltEmail, setIncludeAltEmail] = useState(true);
  const [includeAltEmail2, setIncludeAltEmail2] = useState(true);
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>([]);

  // Channel toggle — staff can elect SMS instead of email when the member
  // has an active Account & service SMS opt-in AND a phone on file. The
  // toggle is hidden when either is missing.
  const [composeChannel, setComposeChannel] = useState<"email" | "sms">("email");
  const [smsBody, setSmsBody] = useState("");
  const [sendingSms, setSendingSms] = useState(false);
  const [smsOptedIn, setSmsOptedIn] = useState<boolean>(false);
  // Staff composing one message to this member (PM 9123a167): the backend
  // needs send_member_message plus the channel permission (send_email for an
  // email, send_sms for a text). Without them the composer is not shown.
  // send_member_message counts as held while it is not defined in the club,
  // as on the server.
  const staffMayEmail = useHasLayeredPermission("send_email", "send_member_message");
  const staffMayText = useHasLayeredPermission("send_sms", "send_member_message");
  const staffMayNotEmail = source !== "tenant" && !staffMayEmail;
  const canSendSms =
    source !== "tenant" &&
    smsOptedIn &&
    !!membership?.phone &&
    staffMayText;

  // Quick-reply presets — only shown when staff is composing (source !== "tenant").
  // Driven by `Quick: %` rows in email_templates so tenants can add new
  // presets via the existing template editor. The hardcoded set is the
  // fallback for the brief window before the API call resolves and for
  // any failure — same 4 entries the backend uses as its baseline.
  const FALLBACK_QUICK_REPLY_PRESETS: { label: string; notificationKey: string }[] = [
    { label: "Past Due", notificationKey: "Quick: Past Due" },
    { label: "Welcome", notificationKey: "Quick: Welcome" },
    { label: "Class Reminder", notificationKey: "Quick: Class Reminder" },
    { label: "Receipt", notificationKey: "Quick: Receipt" },
  ];
  const [quickReplyPresets, setQuickReplyPresets] = useState(FALLBACK_QUICK_REPLY_PRESETS);

  useEffect(() => {
    // Members (tenant source) never see the quick-reply composer, and the
    // endpoint is staff-gated — fetching for them just generated a 403 per
    // member page view (surfaced by the permission_denied telemetry,
    // 2026-07-04). Only fetch when the presets will actually render.
    if (!token || source === "tenant") return;
    let cancelled = false;
    getQuickReplyTemplates(token).then((rows) => {
      if (cancelled || !Array.isArray(rows) || rows.length === 0) return;
      setQuickReplyPresets(
        rows.map((r: { label: string; notification_key: string }) => ({
          label: r.label,
          notificationKey: r.notification_key,
        }))
      );
    });
    return () => { cancelled = true; };
  }, [token, source]);

  // Rich-HTML email body → plain text suitable for an SMS body. Preserves
  // paragraph/line breaks, decodes a few common entities, normalizes
  // runs of whitespace, and dedupes blank lines. Quick-reply templates
  // are still authored once as email HTML; this lets them double as
  // SMS drafts without forking templates per channel.
  const htmlToSmsPlainText = (html: string): string => {
    if (!html) return "";
    const withBreaks = html
      .replace(/<\s*br\s*\/?>/gi, "\n")
      .replace(/<\/(p|div|li|h[1-6]|tr)>/gi, "\n")
      .replace(/<li[^>]*>/gi, "- ");
    const noTags = withBreaks.replace(/<[^>]+>/g, "");
    const decoded = noTags
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");
    return decoded
      .split("\n")
      .map((line) => line.replace(/[ \t]+/g, " ").trim())
      .filter((line, i, arr) => !(line === "" && arr[i - 1] === ""))
      .join("\n")
      .trim();
  };

  const handleApplyPreset = async (notificationKey: string) => {
    if (!memberId || loadingPreset) return;
    setLoadingPreset(notificationKey);
    try {
      const res: any = await renderTemplateForMember(token, {
        notificationKey,
        memberId,
      });
      const payload = res?.data;
      if (res?.success && payload?.success) {
        if (composeChannel === "sms") {
          // Same template, rendered down to SMS-safe plain text. Staff
          // can review/trim before sending; we don't enforce a length
          // limit at preset-application time so they see the full draft.
          const plain = htmlToSmsPlainText(payload.body || "");
          if (plain) {
            setSmsBody(plain);
            if (plain.length > 1600) {
              toast.warning("Template is longer than 1600 chars — trim before sending.");
            } else if (plain.length > 320) {
              toast.info(
                `${plain.length} chars (~${Math.ceil(plain.length / 160)} segments). Trim to keep SMS short.`
              );
            }
          } else {
            toast.error("Template body was empty after converting to plain text.");
          }
        } else {
          if (payload.subject) setEmailSubject(payload.subject);
          if (payload.body) setEmailBody(payload.body);
        }
      } else {
        toast.error(res?.error || payload?.error || "Could not load template");
      }
    } catch (err: any) {
      toast.error("Could not load template: " + (err?.message || "unknown error"));
    } finally {
      setLoadingPreset(null);
    }
  };

  const handleClearComposer = () => {
    if (composeChannel === "sms") {
      setSmsBody("");
      return;
    }
    const storedClubName = localStorage.getItem("clubName");
    const defaultClubName =
      clubName ||
      storedClubName ||
      (settings?.business_name as string | undefined) ||
      "Athletics Club";
    setEmailSubject(t("messages:compose.defaultSubject", { clubName: defaultClubName }));
    setEmailBody("");
  };

  const fetchMessages = async () => {
    if (!memberId) return;
    setLoading(true);
    try {
      const res = await getMemberMessages(memberId, token);
      if (res?.success) {
        setMessages(res?.data);
      }
    } catch (err) {
      // Axios interceptor handles Sentry capture
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, [memberId]);

  // Pull the recipient's communication preferences so we know whether the
  // SMS channel toggle is allowed. Backend gates the actual send on the
  // same opt-in; this read just controls UI visibility.
  useEffect(() => {
    if (source === "tenant") return;
    const authId = membership?.auth_id;
    if (!authId || !token) {
      setSmsOptedIn(false);
      return;
    }
    let cancelled = false;
    axios
      .get(`${apiConfig.BASE_URL}/users/${authId}/communication-preferences`, {
        headers: {
          "Content-Type": "application/json",
          "x-tenant-id": getTenantId(),
          "x-auth-source": getAuthSource(),
          authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        if (cancelled) return;
        const prefs = res?.data?.data;
        setSmsOptedIn(!!prefs?.sms_transactional_active);
      })
      .catch(() => {
        if (!cancelled) setSmsOptedIn(false);
      });
    return () => {
      cancelled = true;
    };
  }, [membership?.auth_id, source, token]);

  const handleSendSms = async () => {
    const trimmed = smsBody.trim();
    if (!trimmed) {
      toast.error("Type a message first.");
      return;
    }
    if (trimmed.length > 1600) {
      toast.error("Message is too long (max 1600 characters).");
      return;
    }
    try {
      setSendingSms(true);
      const response = await sendMemberSms(token, {
        memberId,
        message: trimmed,
        userId: userDetails?.id,
        user: userDetails,
        // source mirrors the email path so the backend can NULL user_id
        // for staff sends (their userDetails.id is a central-DB id and
        // wouldn't satisfy the tenant member_messages.user_id FK).
        source,
      });
      if (response?.success && response?.held) {
        // Quiet hours where the family lives (PM 9da49135): the server says
        // when it will go ("Held until Thu 8:00 AM their time. ...").
        toast.info(response?.message || "Held until morning. It is quiet hours where this family lives.");
        setSmsBody("");
        fetchMessages();
      } else if (response?.success) {
        toast.success("SMS sent");
        setSmsBody("");
        fetchMessages();
      } else {
        toast.error(response?.error || "Failed to send SMS");
      }
    } catch (error: any) {
      toast.error("Failed to send SMS: " + (error?.message || ""));
    } finally {
      setSendingSms(false);
    }
  };

  const handleShowMore = () => {
    setVisibleCount((prev) => prev + 4); // load 5 more at a time
  };

  // Send Email function
  const handleSendEmail = async () => {
    const strippedBody = emailBody.replace(/<[^>]*>/g, '').trim();
    if (!emailSubject.trim() || !strippedBody) {
      toast.error(t('messages:validation.fillBoth'));
      return;
    }

    try {
      setSending(true);
      const storedClubName = localStorage.getItem("clubName");
      const response = await sendEmail(token, {
        to: membership?.email,
        subject: emailSubject,
        message: emailBody,
        memberId: memberId,
        userId: userDetails?.id,
        source: source,
        user: userDetails,
        clubName: storedClubName,
        // Recipient picker: each toggle decides whether that address
        // gets CC'd on this specific send. Server validates contact IDs
        // belong to the member before resolving them to emails.
        include_alt_email_1: includeAltEmail,
        include_alt_email_2: includeAltEmail2,
        additional_contact_ids: selectedContactIds,
      });

      if (response.status === "success") {
        toast.success(t('messages:notifications.sendSuccess'));
        setEmailSubject("");
        setEmailBody("");
        // Refresh messages to show the new one
        fetchMessages();
      } else if (response?.delivery_failed) {
        // Saved to Message History, but the mail service refused it. Say so
        // with the server's own reason, and show the new "Not sent" entry.
        // (2026-10-10: this said "Email sent successfully!".)
        toast.error(response.error || t('messages:notifications.sendError'));
        fetchMessages();
      } else {
        toast.error(t('messages:notifications.sendError'));
      }
    } catch (error: any) {
      toast.error(t('common:messages.error') + ": " + error.message);
    } finally {
      setSending(false);
    }
  };

  // Load settings from localStorage and set default subject
  useEffect(() => {
    const storedSettings = localStorage.getItem("settings");
    const storedClubName = localStorage.getItem("clubName");

    if (storedSettings) {
      try {
        setSettings(JSON.parse(storedSettings));
      } catch (error) {
        captureError(error, { component: 'MessagesHistoryPage', operation: 'parseSettings' });
      }
    }

    // Set default subject if not already set
    if (!emailSubject) {
      const defaultClubName =
        clubName ||
        storedClubName ||
        (storedSettings ? JSON.parse(storedSettings)?.business_name : null);
      const defaultSubject = t('messages:compose.defaultSubject', { clubName: defaultClubName || 'Athletics Club' });
      setEmailSubject(defaultSubject);
    }
  }, [clubName, emailSubject, t]);

  // Delete email
  const handleDelete = async () => {
    try {
      const res = await deleteEmailMessages(token, deletemessageId);
      if (res?.success) {
        toast.success(t('messages:notifications.deleteSuccess'));
        setIsModalOpen(false)
        setMessages((prev:any) => prev.filter((n:any) => n.id !== deletemessageId));
      }
    } catch (err: any) {
      toast.error(t('messages:notifications.deleteError') + ": " + err.message);
    } finally {
      setDeleteMessageId(null);
    }
  };
  
  // Custom image handler - uses ref to keep modules object stable
  const imageHandlerRef = useRef<() => void>();
  imageHandlerRef.current = () => setMediaLibraryOpen(true);

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'align': [] }],
        ['link', 'image'],
        ['clean']
      ],
      handlers: {
        image: function() { imageHandlerRef.current?.(); }
      }
    }
  }), []);

  // Handle image selection from MediaLibrary - insert into editor
  const handleImageSelect = useCallback((imageUrl: string) => {
    const editor = quillRef.current?.getEditor();
    if (editor) {
      const range = editor.getSelection(true);
      editor.insertEmbed(range.index, 'image', imageUrl);
      editor.setSelection(range.index + 1);
    }
    setMediaLibraryOpen(false);
  }, []);

  // Intercept paste to upload images to storage instead of embedding base64
  useEffect(() => {
    if (loading) return;
    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    const handlePaste = async (e: ClipboardEvent) => {
      const items = Array.from(e.clipboardData?.items || []);
      const imageItem = items.find(item => item.type.startsWith('image/'));
      if (!imageItem) return;

      e.preventDefault();
      e.stopPropagation();

      const file = imageItem.getAsFile();
      if (!file) return;

      try {
        const ext = file.type.split('/')[1] || 'png';
        // Through the backend (signed upload URL), never the anon client
        // (PM 65d0edd2).
        const { publicUrl } = await uploadMediaFile("emails", file, {
          fileName: `pasted-image.${ext}`,
          contentType: file.type,
        });

        const range = editor.getSelection(true);
        editor.insertEmbed(range.index, 'image', publicUrl);
        editor.setSelection(range.index + 1);
      } catch (err) {
        toast.error("Failed to upload pasted image");
        captureError(err, { component: 'MessagesHistoryPage', operation: 'pasteImage' });
      }
    };

    editor.root.addEventListener('paste', handlePaste);
    return () => editor.root.removeEventListener('paste', handlePaste);
  }, [loading]);

  if (loading) {
    return (
      <div
        className="w-full flex justify-center items-center"
        style={{ height: "calc(300px)" }}
      >
        {/* <LoadingSpinner size="large" color="blue" /> */}
        <div className="flex justify-center items-center mb-4">
          <div className="w-6 h-6 border-2 border-gray-400 dark:border-gray-500 border-t-transparent rounded-full animate-spin"></div>
          <span className="ml-2 text-gray-500 dark:text-gray-400">{t('common:messages.loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`mt-6 sm:mt-8 w-full ${getCardBackground(isDarkMode)}`}>
      {/* Section Heading - only show for members (tenant source) */}
      {source === "tenant" && (
        <div className="mb-4 sm:mb-6">
          <h3 className={`text-lg sm:text-xl font-semibold ${getPrimaryText(isDarkMode)}`}>
            {t('messages:contactUs.title')}
          </h3>
          <p className={`text-sm ${getSecondaryText(isDarkMode)} mt-1`}>
            {t('messages:contactUs.subtitle')}
          </p>
        </div>
      )}

      {/* Hidden subject field */}
      <input
        type="hidden"
        value={emailSubject}
        onChange={(e) => setEmailSubject(e.target.value)}
      />

      {/* AI Assistant — staff-only one-shot draft using member context.
          Use the memberId PROP (which is the membership being edited) — not
          the Redux membership.id, which can drift to the staff user's own
          membership when Redux hasn't been re-hydrated for the current edit
          context. Same fix is applied to the sendEmail memberId below. */}
      {source !== "tenant" && memberId && composeChannel === "email" && !staffMayNotEmail && (
        <AIDraftPanel
          membershipId={memberId}
          memberName={[membership?.first_name, membership?.last_name].filter(Boolean).join(" ") || "this member"}
          isDarkMode={isDarkMode}
          token={token}
          onApply={(subject, body) => {
            if (subject) setEmailSubject(subject);
            if (body) setEmailBody(body);
          }}
        />
      )}

      {/* Channel toggle — staff only, and only when the member has opted
          in to Account & service SMS and has a phone on file. Email stays
          the default; SMS is opt-in for both the sender and the recipient. */}
      {canSendSms && (
        <div className="mb-3 inline-flex rounded-md border overflow-hidden text-sm" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={composeChannel === "email"}
            onClick={() => setComposeChannel("email")}
            className={`px-3 py-1.5 transition-colors ${
              composeChannel === "email"
                ? "bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-900"
                : isDarkMode
                ? "bg-gray-700 text-gray-200 hover:bg-gray-600"
                : "bg-white text-gray-700 hover:bg-gray-50"
            }`}
          >
            Email
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={composeChannel === "sms"}
            onClick={() => setComposeChannel("sms")}
            className={`px-3 py-1.5 transition-colors border-l ${getLightBorder(isDarkMode)} ${
              composeChannel === "sms"
                ? "bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-900"
                : isDarkMode
                ? "bg-gray-700 text-gray-200 hover:bg-gray-600"
                : "bg-white text-gray-700 hover:bg-gray-50"
            }`}
          >
            SMS
          </button>
        </div>
      )}

      {/* Quick-reply preset buttons (staff only) — pre-fills the composer
          with a tenant template rendered against the current member. In
          SMS mode the rendered HTML body is collapsed to plain text and
          loaded into the SMS textarea instead of the email body. */}
      {source !== "tenant" && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className={`text-xs font-medium ${getSecondaryText(isDarkMode)} mr-1`}>
            Quick replies:
          </span>
          {quickReplyPresets.map((preset) => {
            const isLoading = loadingPreset === preset.notificationKey;
            return (
              <button
                key={preset.notificationKey}
                type="button"
                onClick={() => handleApplyPreset(preset.notificationKey)}
                disabled={loadingPreset !== null || sending || !memberId}
                className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${
                  isLoading ? "opacity-70 cursor-wait" : ""
                } ${
                  loadingPreset !== null && !isLoading ? "opacity-50 cursor-not-allowed" : ""
                } ${
                  isDarkMode
                    ? "border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600"
                    : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                }`}
                title={`Load the "${preset.label}" template for this member`}
              >
                {isLoading ? "Loading…" : preset.label}
              </button>
            );
          })}
          <Tooltip content="Clear the composer to write a custom message"><button
            type="button"
            onClick={handleClearComposer}
            disabled={loadingPreset !== null || sending}
            className={`text-xs px-3 py-1.5 rounded-md transition-colors ${
              isDarkMode
                ? "text-gray-300 hover:bg-gray-700"
                : "text-gray-500 hover:bg-gray-100"
            }`}

          >
            Clear
          </button></Tooltip>
        </div>
      )}

      {/* Recipient picker — staff only. Lets the sender pick who on the
          membership receives this specific message. Primary always goes;
          alternates default ON (matching legacy behavior); additional
          contacts default OFF since adding outside parties to a single
          message is opt-in. Hidden when the member is messaging staff or
          when composing SMS (SMS goes to the member's primary phone only). */}
      {source !== "tenant" && membership && composeChannel === "email" && !staffMayNotEmail && (
        <div className={`mb-3 p-3 rounded border ${getLightBorder(isDarkMode)} ${getCardBackground(isDarkMode)}`}>
          <p className={`text-xs font-medium mb-2 ${getSecondaryText(isDarkMode)}`}>
            Recipients
          </p>
          <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm">
            <span className={getPrimaryText(isDarkMode)}>
              <strong>To:</strong> {membership?.email || "(no primary email)"}
            </span>
            {membership?.alternate_email && (
              <label className={`inline-flex items-center gap-2 ${getPrimaryText(isDarkMode)}`}>
                <input
                  type="checkbox"
                  checked={includeAltEmail}
                  onChange={(e) => setIncludeAltEmail(e.target.checked)}
                  className="rounded"
                />
                <span>CC: {membership.alternate_email}</span>
              </label>
            )}
            {membership?.alternate_email_2 && (
              <label className={`inline-flex items-center gap-2 ${getPrimaryText(isDarkMode)}`}>
                <input
                  type="checkbox"
                  checked={includeAltEmail2}
                  onChange={(e) => setIncludeAltEmail2(e.target.checked)}
                  className="rounded"
                />
                <span>CC: {membership.alternate_email_2}</span>
              </label>
            )}
            {(membership?.additional_contacts || [])
              .filter((c: any) => c?.email)
              .map((c: any) => {
                const checked = selectedContactIds.includes(c.id);
                const name = [c.first_name, c.last_name].filter(Boolean).join(" ") || c.email;
                return (
                  <label key={c.id} className={`inline-flex items-center gap-2 ${getPrimaryText(isDarkMode)}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        setSelectedContactIds((prev) =>
                          e.target.checked
                            ? [...prev, c.id]
                            : prev.filter((id) => id !== c.id)
                        );
                      }}
                      className="rounded"
                    />
                    <span>+ {name} &lt;{c.email}&gt;</span>
                  </label>
                );
              })}
          </div>
        </div>
      )}

      {/* Send Email Form */}
      <style>{`
        .quill-message-history .ql-tooltip {
          z-index: 10;
          left: 0 !important;
        }
        .quill-message-history .ql-tooltip input[type="text"] {
          width: 200px;
        }
      `}</style>
      {composeChannel === "email" && staffMayNotEmail ? (
        <p data-testid="no-member-message-permission" className={`mb-4 text-sm ${getSecondaryText(isDarkMode)}`}>
          Your role does not include sending a message to one member. A club owner can turn on
          &quot;Send Member Message&quot; (with Send Email, or Send SMS for texts) in Roles &amp; Permissions.
        </p>
      ) : composeChannel === "email" ? (
        <div className="mb-4 overflow-visible quill-message-history">
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={emailBody}
            onChange={setEmailBody}
            placeholder={t('messages:compose.placeholder')}
            className={isDarkMode ? 'quill-dark' : ''}
            style={{
              backgroundColor: isDarkMode ? '#374151' : '#ffffff',
              color: isDarkMode ? '#f3f4f6' : '#000000'
            }}
            modules={modules}
          />
          <div className="flex justify-end mt-2">
            <button
              type="button"
              onClick={handleSendEmail}
              disabled={sending}
              className={`bg-[var(--cc-action)] text-[var(--cc-action-text)] hover:bg-[var(--cc-action-hover)] px-4 py-2 rounded-md transition-colors flex items-center justify-center ${
                sending ? "opacity-70 cursor-not-allowed" : ""
              }`}
            >
              {sending ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>{t('messages:compose.sending')}</span>
                </div>
              ) : (
                t('common:buttons.sendEmail')
              )}
            </button>
          </div>
        </div>
      ) : (
        // SMS composer — plain text only. 160-char GSM segments; we cap at
        // ~10 segments (1600 chars) to avoid surprise multi-message fan-out.
        <div className="mb-4">
          <div className={`mb-2 p-3 rounded border ${getLightBorder(isDarkMode)} ${getCardBackground(isDarkMode)}`}>
            <p className={`text-xs font-medium mb-1 ${getSecondaryText(isDarkMode)}`}>To</p>
            <span className={`text-sm ${getPrimaryText(isDarkMode)}`}>
              {membership?.phone || "(no phone on file)"}
            </span>
            <p className={`text-xs mt-2 ${getSecondaryText(isDarkMode)}`}>
              Account &amp; service SMS — plain text only. Msg &amp; data rates may apply.
              Recipient can reply STOP to opt out.
            </p>
          </div>
          <textarea aria-label="Text message"
            value={smsBody}
            onChange={(e) => setSmsBody(e.target.value)}
            placeholder="Type your text message…"
            rows={4}
            maxLength={1600}
            className={`w-full rounded-md p-3 text-sm ${getInputStyles(isDarkMode)}`}
          />
          <div className="flex items-center justify-between mt-2">
            <span className={`text-xs ${getSecondaryText(isDarkMode)}`}>
              {smsBody.length} / 1600 chars · {Math.max(1, Math.ceil(smsBody.length / 160))} segment
              {Math.max(1, Math.ceil(smsBody.length / 160)) === 1 ? "" : "s"}
            </span>
            <button
              type="button"
              onClick={handleSendSms}
              disabled={sendingSms || !smsBody.trim()}
              className={`bg-[var(--cc-action)] text-[var(--cc-action-text)] hover:bg-[var(--cc-action-hover)] px-4 py-2 rounded-md transition-colors flex items-center justify-center ${
                sendingSms || !smsBody.trim() ? "opacity-70 cursor-not-allowed" : ""
              }`}
            >
              {sendingSms ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Sending…</span>
                </div>
              ) : (
                "Send SMS"
              )}
            </button>
          </div>
        </div>
      )}

      {/* Messages History */}
      {messages?.length === 0 ? (
        <p className={getSecondaryText(isDarkMode)}>{t('messages:history.noMessages')}</p>
      ) : (
        <div className="space-y-4">
          {messages?.slice(0, visibleCount)?.map((msg: any) => (
            <div
              key={msg?.id}
              className={`p-4 border ${getLightBorder(isDarkMode)} rounded-md ${getCardBackground(isDarkMode)}`}
            >
              <div className="flex justify-between items-center mb-2">
                <div className="flex items-center gap-2">
                  <span className={`text-sm ${getSecondaryText(isDarkMode)}`}>
                    {formatClubInstant(msg?.created_at, CLUB_LONG_DATE)}
                  </span>
                  {/* Channel Badge — only mark SMS; email is the visual default. */}
                  {msg?.type === "sms" && (
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                      isDarkMode ? "bg-purple-900 text-purple-200" : "bg-purple-100 text-purple-800"
                    }`}>
                      SMS
                    </span>
                  )}
                  {/* Status Badge */}
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusBadgeClasses(msg?.status, isDarkMode)}`}>
                    {formatStatusLabel(msg?.status)}
                  </span>
                </div>
                <span className={`text-sm ${getPrimaryText(isDarkMode)} font-semibold`}>
                  {t('messages:history.sentBy')}{" "}
                  {getSenderLabel(msg, t)}
                </span>
              </div>
              <div className="flex-1">
                <h4 className={`text-md font-semibold ${getPrimaryText(isDarkMode)}`}>{msg?.subject}</h4>
                <p className={`${getPrimaryText(isDarkMode)} whitespace-pre-line text-sm mt-1`}>
                  {msg?.message?.replace(/<style[\s\S]*?<\/style>/gi, '').replace(/<br\s*\/?>/gi, '\n').replace(/<\/p>/gi, '\n').replace(/<[^>]*>/g, '').replace(/\s{2,}/g, ' ').trim().slice(0, 300)}{msg?.message?.length > 300 ? '...' : ''}
                </p>
                {/* Tracking Info */}
                <div className={`mt-2 text-xs ${getSecondaryText(isDarkMode)} flex flex-wrap gap-3`}>
                  {msg?.delivered_at && (
                    <span>Delivered: {formatClubInstant(msg.delivered_at, CLUB_DATE_TIME)}</span>
                  )}
                  {msg?.opened_at && (
                    <span className="text-green-600">Opened: {formatClubInstant(msg.opened_at, CLUB_DATE_TIME)}</span>
                  )}
                  {msg?.clicked_at && (
                    <span className="text-blue-600">Clicked: {formatClubInstant(msg.clicked_at, CLUB_DATE_TIME)}</span>
                  )}
                  {msg?.status === "held" && msg?.metadata?.release_at && (
                    <span className={isDarkMode ? "text-amber-300" : "text-amber-700"}>
                      Goes out: {formatClubInstant(msg.metadata.release_at, CLUB_DATE_TIME)} (club time)
                    </span>
                  )}
                  {msg?.status === "canceled" && msg?.error_message && (
                    <span className="text-red-500">Not sent: {msg.error_message}</span>
                  )}
                  {msg?.status === "bounced" && msg?.error_message && (
                    <span className="text-red-500">Bounce: {msg.error_message}</span>
                  )}
                  {msg?.status === "delivery_failed" && (
                    <span className="text-red-500">
                      {/* The plain reason from the server; the technical
                          detail (error_message, e.g. "HTTP 401:
                          Unauthenticated.") stays in the stored record. */}
                      {t('messages:history.notSent', 'Not sent')}: {msg?.failure_reason || "The email could not be sent. Nothing was sent."}
                    </span>
                  )}
                </div>
                {/* Action buttons */}
                <div className="mt-2 flex items-center gap-3">
                  {msg?.type !== "sms" && (
                    <button
                      onClick={async () => {
                        setPreviewMessage(msg);
                        setPreviewHtml("");
                        setPreviewLoading(true);
                        try {
                          const res = await getMessagePreview(token, msg.id);
                          if (res?.success) {
                            setPreviewHtml(res.html);
                          } else {
                            setPreviewHtml(msg?.message || "");
                          }
                        } catch {
                          setPreviewHtml(msg?.message || "");
                        } finally {
                          setPreviewLoading(false);
                        }
                      }}
                      className={`text-xs hover:underline ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}
                    >
                      {t('common:buttons.preview', 'Preview Email')}
                    </button>
                  )}
                  <Button
                    type="button"
                    variant="dangerGhost"
                    size="xs"
                    onClick={() => {
                      setDeleteMessageId(msg?.id);
                      setIsModalOpen(true);
                    }}
                  >
                    {t('common:buttons.delete')}
                  </Button>
                </div>
              </div>
            </div>
          ))}

          {visibleCount < messages?.length && (
            <div className="text-center">
              <button
                onClick={handleShowMore}
                className="px-4 py-2 mt-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 rounded-md transition-colors duration-200"
              >
                {t('common:buttons.showMore')}
              </button>
            </div>
          )}
        </div>
      )}
       <ConfirmNotesDeleteModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleDelete}
        title={t('messages:deleteModal.title')}
        message={t('messages:deleteModal.message')}
      />

      {/* Media Library for image insertion */}
      {mediaLibraryOpen && (
        <MediaLibrary
          isOpen={mediaLibraryOpen}
          onClose={() => setMediaLibraryOpen(false)}
          onSelect={handleImageSelect}
          parentName="emails"
        />
      )}

      {/* Email Preview Modal */}
      {previewMessage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className={`relative w-full max-w-2xl max-h-[90vh] rounded-lg shadow-xl flex flex-col ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            {/* Modal Header */}
            <div className={`flex items-center justify-between p-4 border-b ${getLightBorder(isDarkMode)}`}>
              <div>
                <h3 className={`text-lg font-semibold ${getPrimaryText(isDarkMode)}`}>
                  {previewMessage?.subject}
                </h3>
                <p className={`text-xs ${getSecondaryText(isDarkMode)} mt-1`}>
                  {t('messages:history.sentBy')}{' '}
                  {getSenderLabel(previewMessage, t)}
                  {' — '}
                  {formatClubInstant(previewMessage?.created_at, CLUB_DATE_TIME)}
                </p>
              </div>
              <Button
                type="button"
                variant="neutral"
                size="sm"
                iconOnly
                aria-label="Close preview"
                onClick={() => { setPreviewMessage(null); setPreviewHtml(""); }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </Button>
            </div>
            {/* Modal Body - iframe for email preview */}
            <div className="flex-1 overflow-hidden p-4">
              {previewLoading ? (
                <div className="flex justify-center items-center" style={{ minHeight: '400px' }}>
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-gray-400 dark:border-gray-500 border-t-transparent rounded-full animate-spin"></div>
                    <span className={getSecondaryText(isDarkMode)}>{t('common:messages.loading')}</span>
                  </div>
                </div>
              ) : (
                <iframe
                  title="Email Preview"
                  srcDoc={previewHtml}
                  className="w-full h-full rounded border"
                  style={{ minHeight: '400px', backgroundColor: '#ffffff' }}
                  sandbox=""
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── AI DRAFT PANEL ───────────────────────────────────────────────────────
// Staff-only assistant that drafts a personalized email using this
// member's facts (balance, enrollments, athletes, payment history).
// Output populates the existing Subject + Body fields; staff reviews
// and sends through the normal flow.

const INTENT_OPTIONS: { value: MemberEmailIntent; label: string }[] = [
  { value: "custom", label: "Free-form (custom goal)" },
  { value: "payment_reminder", label: "Payment reminder" },
  { value: "welcome_back", label: "Welcome / re-engagement" },
  { value: "schedule_change", label: "Schedule change" },
  { value: "follow_up", label: "Follow-up" },
  { value: "congrats", label: "Congratulations / milestone" },
  { value: "reactivation", label: "Reactivate (lapsed family)" },
];

const TONE_OPTIONS: { value: AIDraftTone; label: string }[] = [
  { value: "friendly", label: "Friendly" },
  { value: "formal", label: "Formal" },
  { value: "concise", label: "Concise" },
  { value: "energetic", label: "Energetic" },
];

interface AIDraftPanelProps {
  membershipId: string;
  memberName: string;
  isDarkMode: boolean;
  token: string | null;
  onApply: (subject: string, body: string) => void;
}

const AIDraftPanel: React.FC<AIDraftPanelProps> = ({ membershipId, memberName, isDarkMode, token, onApply }) => {
  const [open, setOpen] = useState(false);
  const [intent, setIntent] = useState<MemberEmailIntent>("custom");
  const [goal, setGoal] = useState("");
  const [tone, setTone] = useState<AIDraftTone>("friendly");
  const [loading, setLoading] = useState(false);
  const [rationale, setRationale] = useState("");

  const handleDraft = async () => {
    if (!token) return;
    if (intent === "custom" && goal.trim().length < 5) {
      toast.error("For a free-form draft, tell the assistant what you want to say.");
      return;
    }
    setLoading(true);
    try {
      const res = await draftMemberEmail(token, {
        membership_id: membershipId,
        intent,
        goal: goal.trim() || undefined,
        tone,
      });
      if (res?.success && res.data) {
        onApply(res.data.subject || "", res.data.body || "");
        setRationale(res.data.rationale || "");
        if (res.data._parse_failed) {
          toast.warning("Draft loaded but couldn't be parsed cleanly — review before sending.");
        } else {
          toast.success("Draft ready — review and edit before sending.");
        }
      } else {
        toast.error(res?.error || "AI draft failed");
      }
    } catch {
      toast.error("AI draft failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mb-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium transition-colors ${isDarkMode ? 'bg-purple-900/40 text-purple-200 hover:bg-purple-900/60' : 'bg-purple-50 text-purple-700 hover:bg-purple-100'}`}
      >
        <span aria-hidden="true">✨</span>
        {open ? "Hide AI Assistant" : "Draft with AI"}
      </button>

      {open && (
        <div className={`mt-2 p-3 rounded-lg border ${isDarkMode ? 'bg-purple-950/30 border-purple-800' : 'bg-purple-50 border-purple-200'}`}>
          <p className={`text-xs mb-2 ${getSecondaryText(isDarkMode)}`}>
            Drafts a personalized email to <strong>{memberName}</strong> using their balance, enrollments, athletes, and payment history. Review before sending — nothing leaves until you hit Send Email.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
            <div>
              <label className={`text-xs block mb-1 ${getSecondaryText(isDarkMode)}`}>Type of email</label>
              <select
                value={intent}
                onChange={(e) => setIntent(e.target.value as MemberEmailIntent)}
                className={`w-full p-1.5 rounded border text-sm ${getInputStyles(isDarkMode)}`}
              >
                {INTENT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={`text-xs block mb-1 ${getSecondaryText(isDarkMode)}`}>Tone</label>
              <select
                value={tone}
                onChange={(e) => setTone(e.target.value as AIDraftTone)}
                className={`w-full p-1.5 rounded border text-sm ${getInputStyles(isDarkMode)}`}
              >
                {TONE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          <textarea aria-label="Additional context for the message"
            rows={2}
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            placeholder={
              intent === "custom"
                ? "e.g. Follow up about Emma missing the last 2 classes — see if everything's OK and offer a makeup."
                : "(Optional) anything specific to mention?"
            }
            className={`w-full p-2 rounded border text-sm ${getInputStyles(isDarkMode)}`}
          />

          <div className="flex items-center justify-end gap-2 mt-2">
            <button
              type="button"
              onClick={handleDraft}
              disabled={loading}
              className="flex items-center gap-2 px-3 py-1.5 rounded text-sm font-semibold bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50"
            >
              {loading ? "Drafting…" : "Draft email"}
            </button>
          </div>
          {rationale && (
            <p className={`text-xs italic mt-2 ${getSecondaryText(isDarkMode)}`}>
              Why this draft: {rationale}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default MessagesHistoryPage;
